<template>
  <div class="project-info ccs-fields">
    <div class="ccs-field ccs-12">
      <div class="field-name">
        Наименование
      </div>
      <ccs-input class="ccs-12 field-value" v-model="fieldsValues.name"
        v-bind="fieldsDefinitions.name" :readOnly="!editing"
        @error="(err) => setFieldError('name', err)"/>
    </div>
    <div class="ccs-field ccs-12">
      <div class="field-name">
         Описание
      </div>
      <ccs-input class="ccs-12 field-value" v-model="fieldsValues.description"
        v-bind="fieldsDefinitions.description" :readOnly="!editing"
        @error="(err) => setFieldError('description', err)"/>
    </div>
    <div class="ccs-field ccs-12">
      <div class="field-name">
        Процент выполнения
      </div>
      <div class="field-value">{{ `${fieldsValues.completeness * 100}%` }}</div>
    </div>
    <div class="ccs-field ccs-12">
      <div class="field-name">
        Дата создания
      </div>
      <div class="field-value">
        {{ getDisplayText(fieldsValues.created_at) }}
      </div>
    </div>
    <div class="ccs-field ccs-12">
      <div class="field-name">
        Ответственный ФНД
      </div>
      <ccs-input class="field-value" v-model="fieldsValues.fnd_id"
        v-bind="fieldsDefinitions.fnd_id" :readOnly="!editing" :items="fndsForSelect"
        @change="onFndSearch" @error="(err) => setFieldError('fnd_id', err)"/>
    </div>
    <div class="ccs-field ccs-12" v-if="editing">
      <div class="field-name">
        Ответственные
      </div>
      <ccs-input v-if="editing" class="ccs-7" v-model="responsibleID"
        v-bind="fieldsDefinitions.responsible" :readOnly="!editing" :disabled="!this.fieldsValues.fnd_id"
        :items="responsiblesForSelect" @change="onResponsibleSearch"
        @error="(err) => setFieldError('responsible', err)"/>
      <div v-if="editing" class="ccs-2 responsibles-button">
        <button class="ccs-button ccs-button--small ccs-12" @click="addResponsible" :disabled="!this.fieldsValues.fnd_id">
          Добавить
        </button>
      </div>
    </div>
    <div class="ccs-field ccs-12">
      <div class="field-name" v-if="!editing">
        Ответственные
      </div>
      <div class="field-name" v-else></div>
      <div class="ccs-tag ccs-tag--normal" v-for="(item, index) in responsibles" :key="index"
        @click="removeResponsible(item.id)">
        {{ item.full_name }}
      </div>
      <div v-if="!editing && (!responsibles || responsibles.length === 0)">
        Не назначены
      </div>
    </div>
    <div class="ccs-layout ccs-layout--no-pad">
      <div v-if="!editing" class="info-button">
        <button v-if="permissionsStatus.isProjectUpdateAllowed"
          class="ccs-button" @click="switchEditing">
          Редактировать
        </button>
      </div>
      <div v-if="!editing">
        <button v-if="permissionsStatus.isProjectUpdateAllowed"
          class="ccs-button ccs-button--blight ccs-button--delete"
          @click="removeProject">
          Удалить
        </button>
      </div>
      <div v-else class="info-button">
        <button class="ccs-button" @click="confirmEditing">
          Сохранить изменения
        </button>
      </div>
      <div v-if="editing">
        <button class="ccs-button ccs-button--light" @click="cancelEditing">
          Отмена
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

import { PermissionsList } from '@/helpers/permissions';

import FormHelper from '@/mixins/FormHelper.js';
import AutocompleteParent from '@/mixins/AutocompleteParent.js';

import Input from '@/components/forms/Input.vue';

export default {
  name: 'ProjectInfo',
  mixins: [AutocompleteParent, FormHelper],
  components: {
    'ccs-input': Input,
  },
  data() {
    return {
      PermissionsList,
      usersForSelect: [],
      fndsForSelect: [],
      responsiblesForSelect: [],
      responsiblesForSelectRawData: [],
      editing: false,
      fieldsDefinitions: {
        name: {
          isValid: false,
          error: '',
          required: true,
          type: "text",
          placeholder: "Введите название",
        },
        description: {
          isValid: false,
          error: '',
          required: false,
          type: "text",
          placeholder: "Введите описание",
        },
        fnd_id: {
          isValid: false,
          validationRules: {
            requiredFields: ['responsible'],
          },
          error: '',
          type: "autocomplete",
          placeholder: "Поиск по коду",
        },
        responsible: {
          isValid: false,
          validationRules: {
            requiredFields: ['fnd_id'],
          },
          error: '',
          type: "autocomplete",
          placeholder: "Начните ввод для поиска",
        },
      },
      fieldsValues: {
        id: 0,
        name: '',
        created_at: new Date(),
        updated_at: new Date(),
        description: '',
        responsible: undefined,
        fnd: undefined,
        fnd_id: undefined,
        attachments: { data: [] }
      },
      responsibles: [],
      responsibleID: undefined,
      dateFormatOptions: {
        day: 'numeric',
        year: 'numeric',
        month: 'numeric',
      },
    };
  },
  computed: {
    ...mapState('session', ['permissions']),
    ...mapState('projects', ['project']),
    ...mapState('events', ['event']),
    ...mapGetters('session', ['permissionsStatus']),
  },
  methods: {
    ...mapActions('projects', ['getProject', 'updateProject', 'deleteProject']),
    switchEditing() {
      if (!this.editing) {
        if (this.fieldsValues.fnd_id === 0) this.fieldsValues.fnd_id = undefined;
        if (this.fieldsValues.responsible_id === 0) this.fieldsValues.responsible_id = undefined;
      }
      this.editing = !this.editing;
    },
    cancelEditing() {
      this.resetProjectInfo();
      this.switchEditing();
    },
    resetProjectInfo() {
      this.fieldsValues = {
        ...this.project,
        fnd_id: !!this.project.fnd ? this.project.fnd.id : 0,
      };
      this.responsibles = !!this.project.responsible ? this.project.responsible.data : [];
      this.fndsForSelect = [{
        prop: !!this.project.fnd ? this.project.fnd.id : 0,
        searchText: !!this.project.fnd ? this.project.fnd.code : 'Не назначено',
      }];
    },
    getDisplayText(data) {
      let date = new Date(data);
      return date.toLocaleString('ru-RU', this.dateFormatOptions);
    },
    addResponsible() {
      const responsible = this.responsiblesForSelectRawData.find(item => item.id === this.responsibleID);
      this.responsibles.push(responsible);
      this.responsibleID = undefined;
    },
    removeResponsible(responsibleID) {
      if (!this.editing) return;
      this.responsibles = this.responsibles.filter(item => item.id !== responsibleID);
    },
    async onResponsibleSearch(text) {
      [this.responsiblesForSelect, this.responsiblesForSelectRawData] = await this.getAutocompleteData(`/v1/events/${this.event.id}/employees/list`,
        `${text}&fnd_id=${this.fieldsValues.fnd_id}&role=block_leader`, 'id', 'email', (item) => `${item.full_name} <${item.email}>`);
    },
    async onUserSearch(text) {
      [this.usersForSelect] = await this.getAutocompleteData('/v1/users/list', text, 'id', 'email', (item) => item.full_name);
    },
    async onFndSearch(text) {
      [this.fndsForSelect] = await this.getAutocompleteData('/v1/fnd/list', text, 'id', 'code', (item) => item.code, ['employees']);
    },
    async confirmEditing() {
      this.fieldsValues.responsible = this.responsibles.reduce((result, item) => {
        result.push(item.id);
        return result;
      }, []);
      if (this.fieldsValues.responsible.length === 0 && !this.project.responsible) {
        this.fieldsValues.responsible = undefined;
      }
      this.validateForm();
      if (this.isFormValid) {
        const changedFields = this.getChangedFields({
          ...this.project,
          fnd_id: this.project.fnd?.id,
        });
        if (Object.keys(changedFields).length === 0) {
          this.cancelEditing();
          return;
        }
        try {
          await this.updateProject({
            ...changedFields,
            id: this.fieldsValues.id,
          });
          this.$toasted.success('Изменения сохранены');
          this.switchEditing();
        } catch (err) {
          this.showServerValidationErrors(err);
        }
      }
    },
    removeProject() {
      this.$toasted.info("Удалить проект?", { action: [{
        text: "Да",
        onClick: (e, toastObject) => {
          this.removeProjectRequest();
          toastObject.goAway(0);
        },
      }, {
        text: "Нет",
        onClick: (e, toastObject) => {
          toastObject.goAway(0);
        },
      }]});
    },
    async removeProjectRequest() {
      try {
        await this.deleteProject(this.fieldsValues.id);
        this.$toasted.success('Проект удален');
        this.$router.back();
      } catch (err) {
        this.$toasted.error(err.message);
        this.cancelEditing();
      }
    },
  },
  watch: {
    project: {
      immediate: true,
      handler(project) {
        this.fieldsValues = {
          ...project,
          fnd_id: !!project.fnd ? project.fnd.id : 0,
        };
        this.responsibles = !!this.project.responsible ? this.project.responsible.data : [];
        this.fndsForSelect = [{
          prop: !!project.fnd ? project.fnd.id : 0,
          searchText: !!project.fnd ? project.fnd.code : 'Не назначено',
        }];
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.project-info {
  min-height: 100%;
  min-width: 100%;
  background: #fff;

  .info-button {
    padding-left: 0;
  }

  .button--add {
    margin-top: 22px;
  }

  .responsibles-button {
    margin-left: 20px;
  }

}
</style>
